import { useCallback, useEffect } from "react"
import { CHAIN_ID } from "@mezo/sdk"
import { ModalBody, ModalFooter } from "@mezo-org/mezo-clay"
import { useAccount } from "wagmi"

import {
  RootState,
  selectTokenDecimals,
  selectWalletAddress,
  useDappSelector,
} from "#/redux-state"
import ApproveAndCallProgressSteps, {
  ApproveAndCallProgressStepsProvider,
  ApproveAndCallProgressStepsSubmitButton,
  useApproveAndCallProgressStepsContext,
} from "#/shared/components/ApproveAndCallProgressSteps"
import FailedActionModalBody from "#/shared/components/modals/FailedActionModalBody"
import WaitingForActionModalBody from "#/shared/components/modals/WaitingForActionModalBody"
import { bridge, ETHEREUM_CHAIN_ID, ETHEREUM_NETWORK } from "#/shared/constants"
import MezoBridge from "#/shared/constants/artifacts/MezoBridge"
import { useFetchMats } from "#/shared/hooks"
import {
  useBridgeERC20,
  useEstimateBridgeAssetFees,
} from "#/shared/hooks/bridge"
import { usePosthogBridgeEvent } from "#/shared/hooks/posthog-events"
import { BridgeAssetFormValue } from "#/shared/types"
import { bigIntToFloat } from "#/shared/utils"
import { SwitchChainButton } from "#/ui/Button"

import { useStepperModalContext } from "../StepperModal"
import { ComponentStepProps } from "../StepperModal/StepperModal"

import BridgeSummary from "./BridgeSummary"

function FailedToApproveTokenError() {
  const { onSubmitStep } = useApproveAndCallProgressStepsContext()

  return (
    <FailedActionModalBody
      title="Something went wrong"
      description="Please try again."
      nextStepButtonLabel="Try again"
      onClickNextStep={onSubmitStep}
    />
  )
}

export default function BridgeERC20({ onClickContinue }: ComponentStepProps) {
  const {
    values: { token, amount },
  } = useStepperModalContext<BridgeAssetFormValue>()

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  const { estimatedTransactionFee, serviceFee } = useEstimateBridgeAssetFees(
    token,
    amount,
  )

  const { address } = useAccount()
  const { fetchMats } = useFetchMats()

  const {
    bridgeErc20: bridgeAsset,
    isPending,
    isError,
    isWaitingForTxReceipt,
    isTxConfirmed,
  } = useBridgeERC20(token, amount, address)

  const bridgeAssetHandler = useCallback(async () => {
    await bridgeAsset({
      // Fetch mats for account after transaction has been completed
      onSuccess: async () => {
        await fetchMats()
      },
    })
  }, [bridgeAsset, fetchMats])

  const walletAddress = useDappSelector(selectWalletAddress)

  const { emitAddFundsCompletedEvent } = usePosthogBridgeEvent()

  const handleAddFundsCompletedEvent = useCallback(() => {
    const assetAmount = bigIntToFloat(amount ?? 0n, tokenDecimals)
    const isERC20Token = token !== "BTC"

    const networkFees = bigIntToFloat(
      estimatedTransactionFee,
      isERC20Token ? 18 : 8,
      7,
    )

    emitAddFundsCompletedEvent(
      token,
      assetAmount,
      walletAddress ?? "",
      "native",
      networkFees,
      bigIntToFloat(serviceFee, 18, 7),
    )
  }, [
    amount,
    emitAddFundsCompletedEvent,
    estimatedTransactionFee,
    serviceFee,
    token,
    tokenDecimals,
    walletAddress,
  ])

  useEffect(() => {
    if (!isTxConfirmed) return

    handleAddFundsCompletedEvent()
    onClickContinue()
  }, [handleAddFundsCompletedEvent, isTxConfirmed, onClickContinue])

  if (isPending || isWaitingForTxReceipt)
    return <WaitingForActionModalBody title="Waiting for transaction..." />

  if (isError)
    return (
      <FailedActionModalBody
        title="Something went wrong"
        description="Please try again."
        nextStepButtonLabel="Try again"
        onClickNextStep={bridgeAssetHandler}
      />
    )

  return (
    <ApproveAndCallProgressStepsProvider
      token={bridge.BRIDGEABLE_TOKENS[token].sourceToken}
      amountToApprove={amount ?? 0n}
      callTransactionCallback={bridgeAssetHandler}
      spender={MezoBridge.address}
      chainId={ETHEREUM_CHAIN_ID}
      errorChildren={<FailedToApproveTokenError />}
    >
      <ModalBody>
        <BridgeSummary />
        <ApproveAndCallProgressSteps
          token={token}
          callTransactionStepTitle="Bridge assets"
        />
      </ModalBody>
      <ModalFooter>
        <SwitchChainButton
          width="100%"
          size="large"
          shape="default"
          label="bridge"
          chainId={CHAIN_ID[ETHEREUM_NETWORK]}
        >
          <ApproveAndCallProgressStepsSubmitButton
            size="large"
            width="100%"
            isLoading={isWaitingForTxReceipt}
          >
            Confirm
          </ApproveAndCallProgressStepsSubmitButton>
        </SwitchChainButton>
      </ModalFooter>
    </ApproveAndCallProgressStepsProvider>
  )
}
